import { Component, inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { UserService } from "../../services/user.service";
import { CreateEndconsumerModel } from "../../models/user.model";
import { AuthService } from "../../services/auth.service";
import { NgxSmartModalService } from "ngx-smart-modal";
import { CommonService } from "../../services/common.service";
import { ActiveTab, TabService } from "../../services/tab-service.service";
import { TranslateService } from "@ngx-translate/core";
import { CountryRestrictionService } from "../../services/country-restriction.service";
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { CountryISO, SearchCountryField } from 'ngx-intl-tel-input';

@Component({
  selector: 'app-sign-up-endconsumer',
  templateUrl: './sign-up-endconsumer.component.html',
  styleUrl: './sign-up-endconsumer.component.scss'
})
export class SignUpEndconsumerComponent implements OnInit {
  private _fb: FormBuilder = inject(FormBuilder);
  private _userService: UserService = inject(UserService);
  private _authService: AuthService = inject(AuthService);
  private _ngxSmartModalService: NgxSmartModalService = inject(NgxSmartModalService);
  private _commonService: CommonService = inject(CommonService);
  private _translateService: TranslateService = inject(TranslateService);
  private _countryRestrictionService: CountryRestrictionService = inject(CountryRestrictionService);
  private _platformId: Object = inject(PLATFORM_ID);

  @Input() set modalData(modalData: any) {
    if (modalData && modalData !== undefined) {
      this.otpVerificationDisplay = modalData.otpVerification;
    }
  }

  registrationForm!: FormGroup;
  otpVerificationDisplay: boolean = false;

  ACTIVE_TAB = ActiveTab;

  phoneNumber!: string;

  error!: boolean;
  loading!: boolean;
  submitted: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  loginBtnDisplay: boolean = false;
  formError: any;
  errorMessage!: string | null;

  isBrowser: boolean = isPlatformBrowser(this._platformId);
  preferredCountries: CountryISO[] = [CountryISO.Norway];
  onlyCountries: CountryISO[] = [];
  selectedCountryISO: CountryISO = CountryISO.Norway;
  readonly searchCountryFields: SearchCountryField[] = [SearchCountryField.Iso2, SearchCountryField.Name];

  constructor() {
    this.formInitialize();
    this.setupPhoneCountryOptions();
  }

  ngOnInit(): void {
  }

  formInitialize(): void {
    this.registrationForm = this._fb.group({
      name: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      birthDate: ['', Validators.required],
      gender: [''],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
      phone: [undefined, [Validators.required]],
      emailSubscription: [false],
      confirmTerms: [false, Validators.requiredTrue]
    },
      { validators: this._commonService.passwordsMatchValidator() });
  }

  private setupPhoneCountryOptions(): void {
    const allowedCountries = this._countryRestrictionService.getAllowedCountries();
    const mappedCountries = this.mapToCountryIso(allowedCountries);
    const allCountries = this.getAllCountryIsoValues();

    this.onlyCountries = this.uniqueCountries(mappedCountries.length > 1 ? mappedCountries : allCountries);
    const initialCountry = this.onlyCountries.some(code => code === CountryISO.Norway)
      ? CountryISO.Norway
      : (this.onlyCountries[0] || CountryISO.Norway);
    this.selectedCountryISO = initialCountry;
    const preferred = this.uniqueCountries([CountryISO.Norway, ...mappedCountries]);
    this.preferredCountries = this.onlyCountries.length > 1 ? preferred : [];
  }

  private mapToCountryIso(codes: string[]): CountryISO[] {
    const allCountryIsoValues = this.getAllCountryIsoValues();
    const validCountries = new Set<CountryISO>(allCountryIsoValues);

    return codes
      .map(code => (code || '').toLowerCase() as CountryISO)
      .filter(code => validCountries.has(code));
  }

  private uniqueCountries(countries: CountryISO[]): CountryISO[] {
    return Array.from(new Set(countries));
  }

  private getAllCountryIsoValues(): CountryISO[] {
    return Object.keys(CountryISO)
      .map(key => CountryISO[key as keyof typeof CountryISO])
      .filter((value): value is CountryISO => typeof value === 'string');
  }

  onSubmit(): void {
    if (this.loading) return;

    this.submitted = true;
    this.error = false;
    this.loading = true;
    this.errorMessage = null;
    this.formError = null;

    if (!this.registrationForm.valid) {
      this.loading = false;
      this.registrationForm.markAllAsTouched();
      return;
    }

    const formData: any = this.registrationForm.value;
    const normalizedPhone: string = this.extractE164Phone(formData.phone);
    if (!normalizedPhone) {
      this.loading = false;
      const phoneControl = this.registrationForm.get('phone');
      phoneControl?.setErrors({ validatePhoneNumber: true });
      phoneControl?.markAsTouched();
      return;
    }

    const phoneCountryCode: string = (this.selectedCountryISO || CountryISO.Norway).toUpperCase();

    const profileData: CreateEndconsumerModel['profile'] = {
      first_name: formData.name as string,
      last_name: formData.lastName as string,
      phone_number: normalizedPhone,
      date_of_birth: formData.birthDate as string,
      email_subscription: !!formData.emailSubscription,
      country_code: phoneCountryCode
    };

    if (formData.gender) {
      profileData.gender = formData.gender as string;
    }

    const userData: CreateEndconsumerModel = {
      email: formData.email as string,
      password: formData.password as string,
      profile: profileData
    };

    this._userService.createEndconsumer(userData).subscribe(result => {
      const accessToken: string = result.tokens.access;
      this._authService.setAccessToken(accessToken);

      this._userService.set(result);

      this.phoneNumber = normalizedPhone;
      this.otpVerificationDisplay = true;
      // this.otpRequest();
    }, error => {
      const details = error.error.detail; // TODO
      this.loading = false;

      const jsonReadyStr: string = `{${details.replace(/^(\w+):/, '"$1":').replace(/'/g, '"')}}`;
      const errorObj = JSON.parse(jsonReadyStr);

      if (error && error.status === 400) {
        this.formError = errorObj;
      } else {
        this.errorMessage = details || this._translateService.instant('END_CONS_SIGN_UP.ERRORS.GENERIC');
        this.error = true;
      }
    });
  }

  moveFocus(currentInput: HTMLInputElement, nextInput: HTMLInputElement): void { // TODO can be removed
    if (currentInput.value.length === currentInput.maxLength) {
      nextInput.focus();
    }
  }

  togglePasswordVisibility(field: 'password' | 'confirmPassword') {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else if (field === 'confirmPassword') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  toLogin() {
    this._ngxSmartModalService.getModal('endConsumerRegModal').close();
    // Open above any existing modals so the login modal is visible on top
    this._ngxSmartModalService.open('loginModal', true);
  }

  private normalizePhoneNumber(phone: string): string {
    if (!phone) {
      return '';
    }

    const digitsOnly = phone.replace(/[^0-9+]/g, '').replace(/^\++/, '');
    if (!digitsOnly) {
      return '';
    }

    return `+${digitsOnly}`;
  }

  private extractE164Phone(phoneValue: any): string {
    if (!phoneValue) {
      return '';
    }

    if (typeof phoneValue === 'object') {
      if (phoneValue.countryCode) {
        this.selectedCountryISO = (phoneValue.countryCode as string).toLowerCase() as CountryISO;
      }

      if (phoneValue.e164Number) {
        return phoneValue.e164Number;
      }

      if (phoneValue.internationalNumber) {
        return this.normalizePhoneNumber(phoneValue.internationalNumber);
      }

      if (phoneValue.number) {
        return this.normalizePhoneNumber(phoneValue.number);
      }
    }

    if (typeof phoneValue === 'string') {
      return this.normalizePhoneNumber(phoneValue);
    }

    return '';
  }

  onPhoneCountryChange(country: CountryISO | { iso2: string } | null | undefined): void {
    const iso = typeof country === 'string'
      ? country
      : country?.iso2;

    if (!iso) {
      return;
    }

    this.selectedCountryISO = iso.toLowerCase() as CountryISO;
  }

  goToTermsConditions(): void {
    const currentLang: string = this._translateService.currentLang;
    const url = `/${currentLang}/terms-and-conditions`;
    window.open(url, '_blank');
  }

  goToPrivacyPolicy(): void {
    const currentLang: string = this._translateService.currentLang;
    const url = `/${currentLang}/privacy-policy`;
    window.open(url, '_blank');
  }
}
